import { useState, useCallback, useMemo, useEffect } from "react";
import { ethers } from "ethers";
import { NETWORK } from "src/config";
import getChain from "src/cryptoUtilities/getChain";

// ABIs
import stakingAbi from "src/cryptoUtilities/stakingAbi";
import perpetualStakingAbi from "src/cryptoUtilities/perpetual-staking-abi";
import newStakeAbi from "src/cryptoUtilities/new-stake-abi";

const useAdminStakingData = (contracts = []) => {
    const [loading, setLoading] = useState(false);
    const [loadingPool, setLoadingPool] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    
    // Initialize poolData from localStorage for instant loading
    const [poolData, setPoolData] = useState(() => {
        try {
            const cached = localStorage.getItem("admin_staking_pool_data");
            return cached ? JSON.parse(cached) : {};
        } catch (e) {
            console.error("Failed to load cached pool data", e);
            return {};
        }
    });

    const [allStakes, setAllStakes] = useState([]);

    const fetchPoolData = useCallback(async (cInfo) => {
        if (!cInfo || !cInfo.address) return;
        
        setLoading(true);
        setLoadingPool(cInfo.address);
        setProgress({ current: 0, total: 0 });

        try {
            const selectedChain = getChain()[NETWORK];
            const rpcUrl = selectedChain.rpcUrls[0];
            const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
            const contract = new ethers.Contract(cInfo.address, cInfo.abi, provider);
            
            let aggregatedStakes = [];
            
            // --- STEP 1: Determine Total Count & Iteration Method ---
            let totalCount = 0;
            let method = null;

            try {
                if (contract.getStakeCount) {
                    const count = await contract.getStakeCount();
                    totalCount = count.toNumber();
                    method = "global";
                    
                }
            } catch (e) { console.log("[LOG] getStakeCount not available"); }

            if (totalCount === 0) {
                try {
                    if (contract.getAllStakers) {
                        const stakers = await contract.getAllStakers();
                        totalCount = stakers.length;
                        method = "stakers_list";
                        
                    } else if (contract.getInvestorCount) {
                        const count = await contract.getInvestorCount();
                        totalCount = count.toNumber();
                        method = "investor_loop";
                        
                    }
                } catch (e) { console.log("[LOG] Investor/Staker counts not available"); }
            }

            setProgress({ current: 0, total: totalCount });

            // --- STEP 2: Execute Iteration with Batching ---
            if (method === "global") {
                const batchSize = 10;
                for (let i = 0; i < totalCount; i += batchSize) {
                    const end = Math.min(i + batchSize, totalCount);
                    const batchPromises = [];
                    for (let j = i; j < end; j++) {
                        batchPromises.push(contract.getStake(j).catch(e => {
                            console.error(`Error fetching stake index ${j}`, e);
                            return null;
                        }));
                    }
                    
                    const results = await Promise.all(batchPromises);
                    results.filter(r => r !== null).forEach((stakeRaw, index) => {
                        aggregatedStakes.push(normalizeStakeData(stakeRaw, cInfo, i + index, stakeRaw.staker || "0x..."));
                    });
                    
                    setProgress(prev => ({ ...prev, current: end }));
                }
            } else if (method === "stakers_list" || method === "investor_loop") {
                let addresses = [];
                if (method === "stakers_list") {
                    addresses = await contract.getAllStakers();
                } else {
                    // Investor addresses are usually fewer, but we can batch these if needed
                    for (let i = 0; i < totalCount; i++) {
                        addresses.push(await contract.getInvestor(i));
                    }
                }

                setProgress({ current: 0, total: addresses.length });
                
                const batchSize = 5; // Address-based lookups are heavier, smaller batch
                for (let idx = 0; idx < addresses.length; idx += batchSize) {
                    const end = Math.min(idx + batchSize, addresses.length);
                    const batchPromises = [];
                    
                    for (let b = idx; b < end; b++) {
                        const staker = addresses[b];
                        batchPromises.push((async () => {
                            try {
                                const sc = contract.getInvestorStakeCount ? await contract.getInvestorStakeCount(staker) : await contract.getUserStakeCount(staker);
                                const stakeCount = sc.toNumber();
                                const stakesForThisUser = [];
                                for (let sId = 0; sId < stakeCount; sId++) {
                                    const stakeRaw = await contract.getInvestorStake(staker, sId);
                                    stakesForThisUser.push(normalizeStakeData(stakeRaw, cInfo, sId, staker));
                                }
                                return stakesForThisUser;
                            } catch (e) {
                                console.error(`Error fetching staker ${staker}`, e);
                                return [];
                            }
                        })());
                    }
                    
                    const batchResults = await Promise.all(batchPromises);
                    batchResults.forEach(res => aggregatedStakes.push(...res));
                    setProgress(prev => ({ ...prev, current: end }));
                }
            }

            setPoolData(prev => {
                const newData = {
                    ...prev,
                    [cInfo.address.toLowerCase()]: aggregatedStakes
                };
                localStorage.setItem("admin_staking_pool_data", JSON.stringify(newData));
                return newData;
            });

        } catch (error) {
            console.error(`[CRITICAL] Pool fetch failed for ${cInfo.name}:`, error);
        } finally {
            setLoading(false);
            setLoadingPool(null);
            setProgress({ current: 0, total: 0 });
        }
    }, []);

    const normalizeStakeData = (raw, cInfo, id, stakerAddr) => {
        // Handle variations in tuple structures
        return {
            staker: raw.staker || raw[7] || raw[8] || raw[2] || stakerAddr,
            contractLabel: cInfo.name,
            contractAddress: cInfo.address,
            stakeId: id,
            amount: ethers.utils.formatEther(raw.amount || raw[0] || "0"),
            startTime: (raw.startTime || raw.startDate || raw[1] || raw[3] || { toNumber: () => 0 }).toNumber(),
            totalClaimed: ethers.utils.formatEther(raw.totalClaimed || raw[3] || raw[6] || 0),
            unstaked: !!(raw.unstaked || raw[4] || (raw.dateWithdrawn && raw.dateWithdrawn > 0)),
            accruedReward: ethers.utils.formatEther(raw.accruedReward || raw[2] || raw[5] || 0),
            stakeBurnt: ethers.utils.formatEther(raw.stakeBurnt || raw[6] || 0),
            dateWithdrawn: (raw.dateWithdrawn || raw[7] || raw[8] || 0).toNumber ? (raw.dateWithdrawn || raw[7] || raw[8] || 0).toNumber() : 0,
        };
    };

    // Background Auto-Sync logic for all pools
    useEffect(() => {
        if (contracts.length > 0) {
            const syncNext = async () => {
                // Find a contract that needs syncing or is "stale" (not in poolData)
                const toSync = contracts.find(c => !poolData[c.address.toLowerCase()]);
                if (toSync && !loading) {
                    await fetchPoolData(toSync);
                }
            };
            syncNext();
        }
    }, [contracts, poolData, loading, fetchPoolData]);

    const displayStakes = useMemo(() => {
        return Object.values(poolData).flat();
    }, [poolData]);

    return { allStakes: displayStakes, poolData, loading, loadingPool, progress, fetchPoolData };
};

export default useAdminStakingData;
